(() => {
  let p4ThreeModules;

  const loadP4ThreeModules = () => {
    if (!p4ThreeModules) {
      p4ThreeModules = Promise.all([
        import("https://esm.sh/three@0.180.0"),
        import("https://esm.sh/three@0.180.0/examples/jsm/loaders/GLTFLoader.js"),
        import("https://esm.sh/three@0.180.0/examples/jsm/loaders/DRACOLoader.js"),
        import("https://esm.sh/three@0.180.0/examples/jsm/controls/OrbitControls.js"),
        import("https://esm.sh/three@0.180.0/examples/jsm/geometries/DecalGeometry.js"),
      ]);
    }
    return p4ThreeModules;
  };

  const initializeP4Configurator = async (root, prefersReducedMotion) => {
    const viewer = root.querySelector("[data-p4-viewer]");
    const canvas = viewer?.querySelector("[data-p4-canvas]");
    const modelUrl = viewer?.dataset.modelUrl;
    if (!viewer || !canvas || !modelUrl || viewer.dataset.ready === "true") return;
    viewer.dataset.ready = "true";

    const loaderText = viewer.querySelector("[data-p4-loader-text]");
    const resetButton = viewer.querySelector("[data-p4-reset-view]");
    const accessoryButtons = [...root.querySelectorAll("[data-p4-accessory]")];
    const accessoryCount = root.querySelector("[data-p4-accessory-count]");
    const colorButtons = [...root.querySelectorAll("[data-p4-color-src]")];
    const priceNode = root.querySelector("[data-p4-price]");
    const addToCartButton = root.querySelector("[data-p4-add-to-cart]");
    const quantityInput = root.querySelector("[data-p4-quantity]");
    const variantMapNode = root.querySelector("[data-p4-variant-map]");
    let variantMap = [];
    let selectedVariantId = addToCartButton?.dataset.defaultVariant || "";
    let selectedColor = colorButtons.find((button) => button.classList.contains("is-active"));
    let selectedAccessory = null;

    try {
      variantMap = variantMapNode ? JSON.parse(variantMapNode.textContent) : [];
    } catch (_error) {
      variantMap = [];
    }

    const syncVariant = (button) => {
      selectedColor = button || selectedColor;
      if (!selectedColor || !variantMap.length) return;
      const names = [selectedColor.dataset.p4Color, selectedColor.dataset.p4ColorJp]
        .filter(Boolean)
        .map((name) => name.toLowerCase());
      const match = variantMap.find((variant) => {
        const title = String(variant.title || "").toLowerCase();
        return variant.available && names.some((name) => title.includes(name));
      });
      if (!match) return;
      selectedVariantId = String(match.id);
      if (priceNode && match.price) priceNode.textContent = match.price;
    };

    colorButtons.forEach((button) => {
      button.addEventListener("click", () => syncVariant(button));
    });

    try {
      if (loaderText) loaderText.textContent = "LOADING ENGINE";
      const [THREE, { GLTFLoader }, { DRACOLoader }, { OrbitControls }, { DecalGeometry }] =
        await loadP4ThreeModules();
      if (!viewer.isConnected) return;

      const scene = new THREE.Scene();
      const camera = new THREE.PerspectiveCamera(32, 1, 0.01, 100);
      const renderer = new THREE.WebGLRenderer({
        canvas,
        alpha: true,
        antialias: true,
        powerPreference: "high-performance",
      });
      renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, 1.75));
      renderer.outputColorSpace = THREE.SRGBColorSpace;
      renderer.toneMapping = THREE.ACESFilmicToneMapping;
      renderer.toneMappingExposure = Number.parseFloat(viewer.dataset.exposure || "1.1");
      renderer.shadowMap.enabled = true;
      renderer.shadowMap.type = THREE.PCFSoftShadowMap;

      scene.add(new THREE.HemisphereLight(0xfffaf2, 0x4c3033, 2.3));
      const keyLight = new THREE.DirectionalLight(0xffffff, 4.1);
      keyLight.position.set(-3, 5, -2.5);
      keyLight.castShadow = true;
      keyLight.shadow.mapSize.set(1024, 1024);
      scene.add(keyLight);
      const rimLight = new THREE.DirectionalLight(0xffc2b7, 2.1);
      rimLight.position.set(3, 2.5, 3.5);
      scene.add(rimLight);

      const controls = new OrbitControls(camera, canvas);
      controls.enableDamping = true;
      controls.dampingFactor = 0.065;
      controls.enablePan = false;
      controls.minPolarAngle = Math.PI * 0.27;
      controls.maxPolarAngle = Math.PI * 0.73;
      const autoRotateEnabled = viewer.dataset.autoRotate === "true" && !prefersReducedMotion;
      controls.autoRotate = autoRotateEnabled;
      controls.autoRotateSpeed = 0.12;
      controls.addEventListener("start", () => {
        controls.autoRotate = false;
      });

      const gltfLoader = new GLTFLoader();
      const dracoLoader = new DRACOLoader();
      dracoLoader.setDecoderPath("https://www.gstatic.com/draco/versioned/decoders/1.5.7/");
      gltfLoader.setDRACOLoader(dracoLoader);
      const loadModel = (url, onProgress) =>
        new Promise((resolve, reject) => gltfLoader.load(url, resolve, onProgress, reject));

      const gltf = await loadModel(modelUrl, (event) => {
        if (!loaderText || !event.total) return;
        const percent = Math.min(99, Math.round((event.loaded / event.total) * 100));
        loaderText.textContent = `LOADING ${percent}%`;
      });
      if (!viewer.isConnected) return;

      const model = gltf.scene;
      scene.add(model);
      const frameMaterials = [];
      const frameMeshes = [];
      model.traverse((object) => {
        if (!object.isMesh) return;
        object.castShadow = true;
        object.receiveShadow = true;
        if (object.material) {
          object.material = object.material.clone();
          object.material.side = THREE.DoubleSide;
          object.material.transparent = false;
          object.material.opacity = 1;
          object.material.depthWrite = true;
          object.material.depthTest = true;
          object.material.needsUpdate = true;
          if (/MX_FRAME/i.test(object.material.name || object.name)) {
            const paintColor = object.material.color.clone();
            object.material.dispose();
            object.material = new THREE.MeshBasicMaterial({
              name: "MX_FRAME",
              color: paintColor,
              side: THREE.DoubleSide,
            });
            // Rebuild the paint response from the visible surface itself.
            // The FBX's baked CAD normals contain diagonal artifacts, so a
            // low-amplitude screen-space normal gives the frame volume while
            // keeping the pearl coat smooth and continuous.
            object.material.onBeforeCompile = (shader) => {
              shader.vertexShader = `varying vec3 vMxPaintPosition;\nvarying vec3 vMxPaintViewPosition;\n${shader.vertexShader}`
                .replace(
                  "#include <worldpos_vertex>",
                  `#include <worldpos_vertex>\n` +
                    `vMxPaintPosition = ( modelMatrix * vec4( transformed, 1.0 ) ).xyz;\n` +
                    `vMxPaintViewPosition = ( modelViewMatrix * vec4( transformed, 1.0 ) ).xyz;`,
                );
              shader.fragmentShader = `varying vec3 vMxPaintPosition;\nvarying vec3 vMxPaintViewPosition;\n${shader.fragmentShader}`
                .replace(
                  "vec3 outgoingLight = reflectedLight.indirectDiffuse;",
                  `vec3 paintBase = reflectedLight.indirectDiffuse;\n` +
                    `float paintLuma = dot( paintBase, vec3( 0.2126, 0.7152, 0.0722 ) );\n` +
                    `float paintIsPearlWhite = smoothstep( 0.68, 0.88, paintLuma );\n` +
                    `vec3 paintDx = dFdx( vMxPaintViewPosition );\n` +
                    `vec3 paintDy = dFdy( vMxPaintViewPosition );\n` +
                    `vec3 paintNormal = normalize( cross( paintDx, paintDy ) );\n` +
                    `if ( paintNormal.z < 0.0 ) paintNormal = -paintNormal;\n` +
                    `vec3 paintKeyDirection = normalize( vec3( -0.38, 0.52, 0.76 ) );\n` +
                    `vec3 paintFillDirection = normalize( vec3( 0.62, -0.18, 0.76 ) );\n` +
                    `float paintKey = max( dot( paintNormal, paintKeyDirection ), 0.0 );\n` +
                    `float paintFill = max( dot( paintNormal, paintFillDirection ), 0.0 );\n` +
                    `float paintFacing = clamp( paintNormal.z, 0.0, 1.0 );\n` +
                    `float paintEdge = pow( 1.0 - paintFacing, 2.4 );\n` +
                    `float paintHeight = smoothstep( -0.72, 0.72, vMxPaintPosition.y );\n` +
                    `float paintWhiteShade = 0.765 + paintKey * 0.145 + paintFill * 0.055 + paintHeight * 0.025;\n` +
                    `float paintColorShade = 0.62 + paintKey * 0.245 + paintFill * 0.075 + paintHeight * 0.025;\n` +
                    `float paintShade = mix( paintColorShade, paintWhiteShade, paintIsPearlWhite );\n` +
                    `vec3 paintClearcoat = mix( paintBase, vec3( 1.0 ), mix( 0.62, 0.48, paintIsPearlWhite ) );\n` +
                    `float paintSoftGloss = pow( paintKey, 7.0 ) * ( 0.055 + paintEdge * 0.035 );\n` +
                    `float paintFineGloss = pow( paintKey, 24.0 ) * mix( 0.13, 0.085, paintIsPearlWhite );\n` +
                    `vec3 outgoingLight = paintBase * paintShade;\n` +
                    `outgoingLight += paintClearcoat * paintSoftGloss;\n` +
                    `outgoingLight += vec3( 1.0 ) * paintFineGloss;\n` +
                    `outgoingLight += paintBase * paintEdge * mix( 0.025, 0.018, paintIsPearlWhite );`,
                );
            };
            object.material.customProgramCacheKey = () => "mx-surface-pearl-v5";
            frameMaterials.push(object.material);
            frameMeshes.push(object);
          }
        }
      });

      const bounds = new THREE.Box3().setFromObject(model);
      const size = bounds.getSize(new THREE.Vector3());
      const center = bounds.getCenter(new THREE.Vector3());
      model.position.sub(center);
      model.updateMatrixWorld(true);
      const centeredBounds = new THREE.Box3().setFromObject(model);
      const centeredSize = centeredBounds.getSize(new THREE.Vector3());
      const target = new THREE.Vector3(0, centeredSize.y * 0.03, 0);
      controls.target.copy(target);
      const verticalFov = THREE.MathUtils.degToRad(camera.fov);
      const fitHeightDistance = centeredSize.y / (2 * Math.tan(verticalFov / 2));

      const logoUrl = viewer.dataset.logoUrl;
      if (logoUrl) {
        try {
          const logoTexture = await new THREE.TextureLoader().loadAsync(logoUrl);
          logoTexture.colorSpace = THREE.SRGBColorSpace;
          logoTexture.anisotropy = Math.min(8, renderer.capabilities.getMaxAnisotropy());
          const logoWidth = centeredSize.z * 0.065;
          const logoHeight = logoWidth / 5.02;
          const logoY = centeredBounds.min.y + centeredSize.y * 0.46;
          const logoZ = centeredBounds.min.z + centeredSize.z * 0.43;
          const logoMaterial = new THREE.MeshBasicMaterial({
            map: logoTexture,
            transparent: true,
            alphaTest: 0.08,
            depthTest: true,
            depthWrite: false,
            side: THREE.FrontSide,
            polygonOffset: true,
            polygonOffsetFactor: -4,
            polygonOffsetUnits: -4,
            toneMapped: false,
          });

          frameMeshes.forEach((frameMesh) => {
            const decalDepth = Math.max(0.035, centeredSize.x * 0.08);
            const decalCandidates = [
              [0.405, 0.43],
              [0.39, 0.46],
              [0.375, 0.49],
              [0.44, 0.38],
              [0.345, 0.54],
            ];
            [-1, 1].forEach((side) => {
              let hit;
              for (const [yFraction, zFraction] of decalCandidates) {
                const rayOrigin = new THREE.Vector3(
                  side < 0 ? centeredBounds.min.x - 0.2 : centeredBounds.max.x + 0.2,
                  centeredBounds.min.y + centeredSize.y * yFraction,
                  centeredBounds.min.z + centeredSize.z * zFraction,
                );
                const rayDirection = new THREE.Vector3(side < 0 ? 1 : -1, 0, 0);
                const raycaster = new THREE.Raycaster(
                  rayOrigin,
                  rayDirection,
                  0,
                  centeredSize.x + 0.5,
                );
                hit = raycaster.intersectObject(frameMesh, false)[0];
                if (hit?.face) break;
              }
              if (!hit?.face) return;

              const surfaceNormal = hit.face.normal
                .clone()
                .transformDirection(frameMesh.matrixWorld)
                .normalize();
              // Build the decal axes from the actual down-tube direction so
              // the wordmark stays parallel to the frame instead of relying
              // on a guessed screen-space rotation.
              const downTubeAxis = new THREE.Vector3(0, -0.1, 0.16).normalize();
              const decalYAxis = new THREE.Vector3()
                .crossVectors(surfaceNormal, downTubeAxis)
                .normalize();
              const decalXAxis = new THREE.Vector3()
                .crossVectors(decalYAxis, surfaceNormal)
                .normalize();
              const decalBasis = new THREE.Matrix4().makeBasis(
                decalXAxis,
                decalYAxis,
                surfaceNormal,
              );
              const projectionRotation = new THREE.Euler().setFromRotationMatrix(decalBasis);
              const logoWorldPosition = hit.point.clone().addScaledVector(
                surfaceNormal,
                0.0005,
              );
              const logoGeometry = new DecalGeometry(
                frameMesh,
                logoWorldPosition,
                projectionRotation,
                new THREE.Vector3(logoWidth, logoHeight, decalDepth),
              );
              if (!logoGeometry.getAttribute("position")?.count) {
                logoGeometry.dispose();
                return;
              }
              const logoDecal = new THREE.Mesh(logoGeometry, logoMaterial.clone());
              logoDecal.name = "MX_BRAND_DECAL";
              logoDecal.renderOrder = 4;
              scene.add(logoDecal);
              viewer.dataset.logoDecalCount = String(
                Number.parseInt(viewer.dataset.logoDecalCount || "0", 10) + 1,
              );
            });
          });
        } catch (logoError) {
          console.warn("Light-P4 logo decal failed to load", logoError);
        }
      }

      const setCameraHome = () => {
        const width = Math.max(viewer.clientWidth, 1);
        const height = Math.max(viewer.clientHeight, 1);
        camera.aspect = width / height;
        const horizontalFov = 2 * Math.atan(Math.tan(verticalFov / 2) * camera.aspect);
        const fitWidthDistance = centeredSize.z / (2 * Math.tan(horizontalFov / 2));
        const distance = Math.max(fitHeightDistance, fitWidthDistance) * 1.1;
        camera.position.set(-distance, target.y + centeredSize.y * 0.04, 0);
        camera.near = Math.max(0.01, distance / 100);
        camera.far = Math.max(50, distance * 20);
        camera.updateProjectionMatrix();
        controls.minDistance = distance * 0.62;
        controls.maxDistance = distance * 2.2;
        controls.target.copy(target);
        controls.autoRotate = autoRotateEnabled;
        controls.update();
      };

      const floor = new THREE.Mesh(
        new THREE.PlaneGeometry(Math.max(size.z, size.x) * 2.5, Math.max(size.z, size.x) * 2.5),
        new THREE.ShadowMaterial({ color: 0x6e3034, opacity: 0.06 }),
      );
      floor.rotation.x = -Math.PI / 2;
      floor.position.y = centeredBounds.min.y - 0.015;
      floor.receiveShadow = true;
      scene.add(floor);

      const applyFrameColor = (hex) => {
        if (!hex) return;
        frameMaterials.forEach((material) => {
          material.color.set(hex);
          material.needsUpdate = true;
        });
      };

      applyFrameColor(selectedColor?.dataset.p4ColorHex);
      syncVariant(selectedColor);
      viewer.addEventListener("p4:color-change", (event) => {
        applyFrameColor(event.detail?.hex);
        syncVariant(event.detail?.button);
      });

      const accessoryModels = new Map();
      const clearAccessory = () => {
        if (selectedAccessory?.parent) selectedAccessory.parent.remove(selectedAccessory);
        selectedAccessory = null;
      };

      accessoryButtons.forEach((button) => {
        button.addEventListener("click", async () => {
          accessoryButtons.forEach((item) => {
            const active = item === button;
            item.classList.toggle("is-active", active);
            item.setAttribute("aria-pressed", String(active));
          });
          clearAccessory();
          const url = button.dataset.p4AccessoryModel;
          if (!url) {
            if (accessoryCount) accessoryCount.textContent = "選択中：0点";
            return;
          }
          button.classList.add("is-loading");
          try {
            let accessory = accessoryModels.get(url);
            if (!accessory) {
              const loaded = await loadModel(url);
              accessory = loaded.scene;
              accessoryModels.set(url, accessory);
            }
            const anchor = model.getObjectByName(button.dataset.p4AccessoryAnchor) || model;
            selectedAccessory = accessory;
            anchor.add(accessory);
            if (accessoryCount) accessoryCount.textContent = "選択中：1点";
          } catch (_error) {
            button.classList.add("is-error");
          } finally {
            button.classList.remove("is-loading");
          }
        });
      });

      const resize = () => {
        const width = Math.max(viewer.clientWidth, 1);
        const height = Math.max(viewer.clientHeight, 1);
        renderer.setSize(width, height, false);
        camera.aspect = width / height;
        camera.updateProjectionMatrix();
      };
      setCameraHome();
      resize();
      const resizeObserver = new ResizeObserver(resize);
      resizeObserver.observe(viewer);
      resetButton?.addEventListener("click", setCameraHome);

      let animationFrame;
      const render = () => {
        controls.update();
        renderer.render(scene, camera);
        animationFrame = window.requestAnimationFrame(render);
      };
      render();
      viewer.classList.add("is-ready");
      if (loaderText) loaderText.textContent = "READY";

      document.addEventListener("shopify:section:unload", (event) => {
        if (!event.target.contains(root)) return;
        window.cancelAnimationFrame(animationFrame);
        resizeObserver.disconnect();
        renderer.dispose();
        dracoLoader.dispose();
      }, { once: true });
    } catch (error) {
      console.error("Light-P4 configurator failed to load", error);
      viewer.classList.add("is-error");
      if (loaderText) loaderText.textContent = "2D PREVIEW";
    }

    addToCartButton?.addEventListener("click", async () => {
      if (!selectedVariantId || addToCartButton.disabled) return;
      addToCartButton.disabled = true;
      addToCartButton.classList.add("is-loading");
      const labelNode = addToCartButton.querySelector("span");
      const originalLabel = labelNode?.textContent || "";
      if (labelNode) labelNode.textContent = "ADDING…";
      const items = [{
        id: Number(selectedVariantId),
        quantity: Math.max(1, Number.parseInt(quantityInput?.value || "1", 10) || 1),
        properties: { "カラー": selectedColor?.dataset.p4ColorJp || "WHITE" },
      }];
      const accessoryVariant = accessoryButtons
        .find((button) => button.classList.contains("is-active"))
        ?.dataset.p4AccessoryVariant;
      if (accessoryVariant) items.push({ id: Number(accessoryVariant), quantity: 1 });

      try {
        const response = await fetch("/cart/add.js", {
          method: "POST",
          headers: { "Content-Type": "application/json", Accept: "application/json" },
          body: JSON.stringify({ items }),
        });
        if (!response.ok) throw new Error("Unable to add configuration to cart");
        window.location.assign("/cart");
      } catch (_error) {
        addToCartButton.classList.add("is-error");
        if (labelNode) labelNode.textContent = "RETRY";
      } finally {
        addToCartButton.disabled = false;
        addToCartButton.classList.remove("is-loading");
        window.setTimeout(() => {
          if (labelNode) labelNode.textContent = originalLabel;
          addToCartButton.classList.remove("is-error");
        }, 1800);
      }
    });
  };

  const initializeHome = (root) => {
    if (!root || root.dataset.marsantsxReady === "true") return;
    root.dataset.marsantsxReady = "true";

    // Keep purchase CTAs out of the homepage's smooth-scroll interactions.
    // The native href remains as a no-JavaScript fallback.
    root.querySelectorAll("[data-p4-detail-link]").forEach((link) => {
      link.addEventListener(
        "click",
        (event) => {
          if (
            event.button !== 0 ||
            event.metaKey ||
            event.ctrlKey ||
            event.shiftKey ||
            event.altKey
          ) {
            return;
          }

          event.preventDefault();
          event.stopImmediatePropagation();
          window.location.assign(link.href);
        },
        true,
      );
    });

    const prefersReducedMotion = window.matchMedia(
      "(prefers-reduced-motion: reduce)",
    ).matches;

    const heroCarousel = root.querySelector("[data-hero-carousel]");
    const heroSlides = [...root.querySelectorAll("[data-hero-slide]")];
    const heroDots = [...root.querySelectorAll("[data-hero-dot]")];
    const heroPrevious = root.querySelector("[data-hero-prev]");
    const heroNext = root.querySelector("[data-hero-next]");

    if (heroCarousel && heroSlides.length > 1) {
      let heroIndex = 0;
      let heroTimer;

      const showHeroSlide = (nextIndex) => {
        heroIndex = (nextIndex + heroSlides.length) % heroSlides.length;
        heroSlides.forEach((slide, index) => {
          const isActive = index === heroIndex;
          slide.classList.toggle("is-active", isActive);
          slide.setAttribute("aria-hidden", String(!isActive));
        });
        heroDots.forEach((dot, index) => {
          const isActive = index === heroIndex;
          dot.classList.toggle("is-active", isActive);
          if (isActive) dot.setAttribute("aria-current", "true");
          else dot.removeAttribute("aria-current");
        });
      };

      const stopHeroAutoplay = () => window.clearInterval(heroTimer);
      const startHeroAutoplay = () => {
        stopHeroAutoplay();
        if (prefersReducedMotion || document.hidden) return;
        heroTimer = window.setInterval(() => showHeroSlide(heroIndex + 1), 6200);
      };

      heroPrevious?.addEventListener("click", () => {
        showHeroSlide(heroIndex - 1);
        startHeroAutoplay();
      });
      heroNext?.addEventListener("click", () => {
        showHeroSlide(heroIndex + 1);
        startHeroAutoplay();
      });
      heroDots.forEach((dot) => {
        dot.addEventListener("click", () => {
          showHeroSlide(Number.parseInt(dot.dataset.heroDot || "0", 10));
          startHeroAutoplay();
        });
      });
      heroCarousel.addEventListener("pointerenter", stopHeroAutoplay);
      heroCarousel.addEventListener("pointerleave", startHeroAutoplay);
      heroCarousel.addEventListener("focusin", stopHeroAutoplay);
      heroCarousel.addEventListener("focusout", startHeroAutoplay);
      document.addEventListener("visibilitychange", () => {
        if (document.hidden) stopHeroAutoplay();
        else startHeroAutoplay();
      });

      showHeroSlide(0);
      startHeroAutoplay();
    }

    const heroMenuItems = [...root.querySelectorAll(".hero-nav-item--has-dropdown")];
    const closeHeroMenus = (exceptItem) => {
      heroMenuItems.forEach((item) => {
        if (item === exceptItem) return;
        item.classList.remove("is-open");
        item.querySelector(":scope > .hero-category-link")?.setAttribute("aria-expanded", "false");
      });
    };

    heroMenuItems.forEach((item) => {
      const trigger = item.querySelector(":scope > .hero-category-link");

      item.addEventListener("pointerenter", () => {
        closeHeroMenus(item);
        item.classList.add("is-open");
        trigger?.setAttribute("aria-expanded", "true");
      });

      item.addEventListener("pointerleave", () => {
        item.classList.remove("is-open");
        trigger?.setAttribute("aria-expanded", "false");
      });

      item.addEventListener("focusin", () => {
        closeHeroMenus(item);
        item.classList.add("is-open");
        trigger?.setAttribute("aria-expanded", "true");
      });

      item.addEventListener("focusout", (event) => {
        if (item.contains(event.relatedTarget)) return;
        item.classList.remove("is-open");
        trigger?.setAttribute("aria-expanded", "false");
      });
    });

    root.addEventListener("keydown", (event) => {
      if (event.key !== "Escape") return;
      closeHeroMenus();
      root.querySelector(".hero-nav-item--has-dropdown:focus-within > .hero-category-link")?.focus();
    });

    const categoryLinks = [...root.querySelectorAll("[data-p4-category-link]")];
    const featureLinks = [...root.querySelectorAll("[data-p4-feature-link]")];
    const categorySection = root.querySelector(".p4-product-showcase");
    const productCards = [...root.querySelectorAll("[data-p4-product-card]")];
    const productFilterEmpty = root.querySelector("[data-p4-product-filter-empty]");
    const productFilterTitle = root.querySelector("[data-p4-product-filter-title]");

    const normalizeCategoryKey = (value) => {
      const key = String(value || "").trim().toLowerCase();
      const aliases = {
        "すべての商品": "all-products",
        "all-products": "all-products",
        "all products": "all-products",
        "電動自転車": "e-bikes",
        "electric-bikes": "e-bikes",
        "electric bikes": "e-bikes",
        "電動キックボード": "e-scooters",
        "electric-scooters": "e-scooters",
        "electric scooters": "e-scooters",
        "アクセサリー": "accessories",
        "accessories": "accessories"
      };
      return aliases[key] || key;
    };

    const activateCategory = (key, updateHistory = false) => {
      const normalizedKey = normalizeCategoryKey(key);
      const targetLink = categoryLinks.find((item) => normalizeCategoryKey(item.dataset.p4CategoryKey) === normalizedKey) || categoryLinks.find((item) => item.classList.contains("is-active")) || categoryLinks[0];
      if (!targetLink) return;
      const resolvedKey = normalizeCategoryKey(targetLink.dataset.p4CategoryKey) || "e-bikes";
      featureLinks.forEach((item) => {
        item.classList.remove("is-active");
        item.removeAttribute("aria-current");
      });
      categoryLinks.forEach((item) => {
        const isActive = item === targetLink;
        item.classList.toggle("is-active", isActive);
        if (isActive) item.setAttribute("aria-current", "page");
        else item.removeAttribute("aria-current");
      });

      let visibleProductCount = 0;
      productCards.forEach((card) => {
        const isVisible = resolvedKey === "all-products" || card.dataset.p4ProductCategory === resolvedKey;
        card.hidden = !isVisible;
        if (isVisible) visibleProductCount += 1;
      });
      if (productFilterEmpty) productFilterEmpty.hidden = visibleProductCount !== 0;
      if (productFilterTitle && visibleProductCount === 0) {
        productFilterTitle.textContent = `${targetLink.dataset.p4CategoryLabel || "商品"}を準備中です`;
      }

      if (updateHistory) {
        const url = new URL(window.location.href);
        url.searchParams.delete("feature");
        url.searchParams.set("category", resolvedKey);
        url.hash = "products";
        window.history.pushState({ p4Category: resolvedKey }, "", url);
      }
    };

    const activateFeature = (key, updateHistory = false) => {
      const targetLink = featureLinks.find((item) => item.dataset.p4FeatureKey === key);
      if (!targetLink) return false;

      categoryLinks.forEach((item) => {
        item.classList.remove("is-active");
        item.removeAttribute("aria-current");
      });
      featureLinks.forEach((item) => {
        const isActive = item === targetLink;
        item.classList.toggle("is-active", isActive);
        if (isActive) item.setAttribute("aria-current", "page");
        else item.removeAttribute("aria-current");
      });

      let visibleProductCount = 0;
      productCards.forEach((card) => {
        const features = String(card.dataset.p4ProductFeatures || "").split(/\s+/).filter(Boolean);
        const isVisible = features.includes(key);
        card.hidden = !isVisible;
        if (isVisible) visibleProductCount += 1;
      });
      if (productFilterEmpty) productFilterEmpty.hidden = visibleProductCount !== 0;
      if (productFilterTitle && visibleProductCount === 0) {
        productFilterTitle.textContent = "この特性の商品を準備中です";
      }

      if (updateHistory) {
        const url = new URL(window.location.href);
        url.searchParams.delete("category");
        url.searchParams.set("feature", key);
        url.hash = "products";
        window.history.pushState({ p4Feature: key }, "", url);
      }
      return true;
    };

    categoryLinks.forEach((link) => {
      link.addEventListener("click", (event) => {
        if (link.dataset.p4CategoryLocal !== "true") return;
        event.preventDefault();
        activateCategory(link.dataset.p4CategoryKey, true);
        categorySection?.scrollIntoView({ behavior: prefersReducedMotion ? "auto" : "smooth", block: "start" });
      });
    });

    // Feature cards are real links.  Do not cancel their native navigation:
    // the URL remains usable even if this script is delayed or unavailable,
    // and the query parameter below restores the corresponding filter after
    // the browser reaches the product section.

    const initialUrl = new URL(window.location.href);
    const initialFeature = initialUrl.searchParams.get("feature");
    const initialCategory = initialUrl.searchParams.get("category");
    if (!activateFeature(initialFeature)) activateCategory(initialCategory || "e-bikes");
    window.addEventListener("popstate", () => {
      const url = new URL(window.location.href);
      const feature = url.searchParams.get("feature");
      const category = url.searchParams.get("category") || "e-bikes";
      if (!activateFeature(feature)) activateCategory(category);
    });

    root.querySelectorAll("[data-p4-product-swatch]").forEach((swatch) => {
      swatch.addEventListener("click", () => {
        const card = swatch.closest("[data-p4-product-card]");
        if (!card) return;
        card.querySelectorAll("[data-p4-product-swatch]").forEach((item) => {
          const isActive = item === swatch;
          item.classList.toggle("is-active", isActive);
          item.setAttribute("aria-pressed", String(isActive));
        });
        const image = card.querySelector(".p4-product-card-image");
        if (image && swatch.dataset.productImage) {
          const finishImageChange = () => image.classList.remove("is-changing");
          image.classList.add("is-changing");
          image.removeAttribute("srcset");
          image.removeAttribute("sizes");
          image.addEventListener("load", finishImageChange, { once: true });
          image.addEventListener("error", finishImageChange, { once: true });
          image.src = swatch.dataset.productImage;
        }
        const price = card.querySelector("[data-p4-card-price]");
        if (price && swatch.dataset.productPrice) price.textContent = swatch.dataset.productPrice;
      });
    });

    const marsHero = root.querySelector("[data-mars-parallax]");
    const heroVideo = root.querySelector("[data-hero-video]");

    if (heroVideo && !prefersReducedMotion) {
      const ensureHeroPlayback = () => {
        if (!heroVideo.paused) return;
        heroVideo.muted = true;
        heroVideo.defaultMuted = true;
        heroVideo.play().catch(() => {});
      };

      heroVideo.addEventListener("canplay", ensureHeroPlayback);
      window.requestAnimationFrame(ensureHeroPlayback);
    }

    if (marsHero && !prefersReducedMotion) {
      let pointerFrame;

      marsHero.addEventListener("pointermove", (event) => {
        const bounds = marsHero.getBoundingClientRect();
        const x = (event.clientX - bounds.left) / bounds.width - 0.5;
        const y = (event.clientY - bounds.top) / bounds.height - 0.5;

        window.cancelAnimationFrame(pointerFrame);
        pointerFrame = window.requestAnimationFrame(() => {
          marsHero.style.setProperty("--mx", x.toFixed(3));
          marsHero.style.setProperty("--my", y.toFixed(3));
        });
      });

      marsHero.addEventListener("pointerleave", () => {
        window.cancelAnimationFrame(pointerFrame);
        marsHero.style.setProperty("--mx", "0");
        marsHero.style.setProperty("--my", "0");
      });
    }

    const revealSections = [
      ...root.querySelectorAll(
        ".performance-screen, .function-screen, .p4-configurator-screen, .product-category-screen, .brand-film-screen, .lifestyle-carousel, .reviews-screen, .media-news-screen",
      ),
    ];

    revealSections.forEach((section) => {
      section.dataset.marsReveal = "";
    });

    if (prefersReducedMotion || !("IntersectionObserver" in window)) {
      revealSections.forEach((section) => section.classList.add("is-visible"));
    } else {
      const revealObserver = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (!entry.isIntersecting) return;
            entry.target.classList.add("is-visible");
            revealObserver.unobserve(entry.target);
          });
        },
        { threshold: 0.12 },
      );
      revealSections.forEach((section) => revealObserver.observe(section));
    }

    const performanceScreen = root.querySelector("[data-performance-screen]");
    const performanceMetrics = [
      ...root.querySelectorAll("[data-performance-metric]"),
    ];

    const activatePerformanceMetric = (metric) => {
      if (!performanceScreen || !metric) return;
      performanceScreen.dataset.activeMetric = metric.dataset.performanceMetric;
      performanceMetrics.forEach((item) => {
        const isActive = item === metric;
        item.classList.toggle("is-active", isActive);
        item.setAttribute("aria-pressed", String(isActive));
      });
    };

    performanceMetrics.forEach((metric) => {
      metric.addEventListener("pointerenter", () =>
        metric.classList.add("is-preview"),
      );
      metric.addEventListener("pointerleave", () =>
        metric.classList.remove("is-preview"),
      );
      metric.addEventListener("focus", () => metric.classList.add("is-preview"));
      metric.addEventListener("blur", () => metric.classList.remove("is-preview"));
      metric.addEventListener("click", () => activatePerformanceMetric(metric));
      metric.addEventListener("keydown", (event) => {
        if (event.key !== "Enter" && event.key !== " ") return;
        event.preventDefault();
        activatePerformanceMetric(metric);
      });
    });

    const performanceValues = [
      ...root.querySelectorAll("[data-performance-value]"),
    ];

    const animatePerformanceValues = () => {
      performanceValues.forEach((valueNode) => {
        if (valueNode.dataset.animated === "true") return;
        valueNode.dataset.animated = "true";
        const target = Number.parseFloat(valueNode.dataset.performanceTarget);
        if (!Number.isFinite(target) || prefersReducedMotion) return;

        const duration = 920;
        const startedAt = window.performance.now();
        const hasDecimal = !Number.isInteger(target);

        const tick = (now) => {
          const progress = Math.min((now - startedAt) / duration, 1);
          const eased = 1 - Math.pow(1 - progress, 3);
          const current = target * eased;
          valueNode.textContent = hasDecimal
            ? current.toFixed(1)
            : String(Math.round(current));
          if (progress < 1) window.requestAnimationFrame(tick);
        };

        window.requestAnimationFrame(tick);
      });
    };

    if (performanceScreen && performanceValues.length) {
      if (prefersReducedMotion || !("IntersectionObserver" in window)) {
        animatePerformanceValues();
      } else {
        const metricObserver = new IntersectionObserver(
          (entries) => {
            if (!entries.some((entry) => entry.isIntersecting)) return;
            animatePerformanceValues();
            metricObserver.disconnect();
          },
          { threshold: 0.3 },
        );
        metricObserver.observe(performanceScreen);
      }
    }

    const productTabs = [...root.querySelectorAll("[data-product-tab]")];
    const productPanels = [...root.querySelectorAll("[data-product-panel]")];

    productTabs.forEach((tab) => {
      tab.addEventListener("click", () => {
        productTabs.forEach((item) => {
          const isActive = item === tab;
          item.classList.toggle("is-active", isActive);
          item.setAttribute("aria-selected", String(isActive));
        });

        productPanels.forEach((panel) => {
          panel.hidden = panel.dataset.productPanel !== tab.dataset.productTab;
        });
      });
    });

    const colorImage = root.querySelector("[data-p4-color-image]");
    const colorName = root.querySelector("[data-p4-color-name]");
    const colorLegend = root.querySelector("[data-p4-color-legend]");
    const colorButtons = [
      ...root.querySelectorAll("[data-p4-color-src]"),
    ];

    colorButtons.forEach((button) => {
      const preload = new Image();
      preload.src = button.dataset.p4ColorSrc;

      button.addEventListener("click", () => {
        if (!colorImage || button.classList.contains("is-active")) return;

        colorButtons.forEach((item) => {
          const isActive = item === button;
          item.classList.toggle("is-active", isActive);
          item.setAttribute("aria-pressed", String(isActive));
        });

        colorImage.classList.add("is-changing");
        window.setTimeout(() => {
          colorImage.addEventListener(
            "load",
            () => colorImage.classList.remove("is-changing"),
            { once: true },
          );
          colorImage.src = button.dataset.p4ColorSrc;
          colorImage.alt = `${button.dataset.p4ColorJp}のLight-P4`;
          if (colorName) colorName.textContent = button.dataset.p4Color;
          if (colorLegend) colorLegend.textContent = button.dataset.p4ColorJp;
          root.querySelector("[data-p4-viewer]")?.dispatchEvent(
            new CustomEvent("p4:color-change", {
              detail: {
                button,
                hex: button.dataset.p4ColorHex,
                name: button.dataset.p4Color,
                label: button.dataset.p4ColorJp,
              },
            }),
          );
          if (colorImage.complete) {
            colorImage.classList.remove("is-changing");
          }
        }, prefersReducedMotion ? 0 : 140);
      });
    });

    const brandVideo = root.querySelector(".brand-film-video");
    const playbackButton = root.querySelector("[data-brand-playback]");
    const soundButton = root.querySelector("[data-brand-sound]");

    if (brandVideo && prefersReducedMotion) {
      brandVideo.pause();
    }

    playbackButton?.addEventListener("click", async () => {
      if (!brandVideo) return;
      if (brandVideo.paused) {
        await brandVideo.play();
      } else {
        brandVideo.pause();
      }
      playbackButton.textContent = brandVideo.paused
        ? playbackButton.dataset.playLabel
        : playbackButton.dataset.pauseLabel;
    });

    soundButton?.addEventListener("click", async () => {
      if (!brandVideo) return;
      brandVideo.muted = !brandVideo.muted;
      soundButton.textContent = brandVideo.muted
        ? soundButton.dataset.soundOnLabel
        : soundButton.dataset.soundOffLabel;

      if (!brandVideo.muted && brandVideo.paused) {
        await brandVideo.play();
        if (playbackButton) {
          playbackButton.textContent = playbackButton.dataset.pauseLabel;
        }
      }
    });

    const carousel = root.querySelector("[data-lifestyle-carousel]");
    const slides = [...root.querySelectorAll("[data-lifestyle-slide]")];
    const pagination = [...root.querySelectorAll("[data-lifestyle-page]")];
    const previousButton = root.querySelector("[data-lifestyle-previous]");
    const nextButton = root.querySelector("[data-lifestyle-next]");
    const pauseButton = root.querySelector("[data-lifestyle-pause]");
    let activeSlide = 0;
    let isPaused = prefersReducedMotion;
    let timer;

    const showSlide = (index) => {
      if (!slides.length) return;
      activeSlide = (index + slides.length) % slides.length;

      slides.forEach((slide, slideIndex) => {
        const isActive = slideIndex === activeSlide;
        slide.classList.toggle("is-active", isActive);
        slide.setAttribute("aria-hidden", String(!isActive));
      });

      pagination.forEach((button, buttonIndex) => {
        const isActive = buttonIndex === activeSlide;
        button.classList.toggle("is-active", isActive);
        if (isActive) {
          button.setAttribute("aria-current", "true");
        } else {
          button.removeAttribute("aria-current");
        }
      });
    };

    const startCarousel = () => {
      window.clearInterval(timer);
      if (isPaused || slides.length < 2) return;
      timer = window.setInterval(() => showSlide(activeSlide + 1), 5600);
    };

    pagination.forEach((button, index) => {
      button.addEventListener("click", () => {
        showSlide(index);
        startCarousel();
      });
    });

    previousButton?.addEventListener("click", () => {
      showSlide(activeSlide - 1);
      startCarousel();
    });

    nextButton?.addEventListener("click", () => {
      showSlide(activeSlide + 1);
      startCarousel();
    });

    pauseButton?.addEventListener("click", () => {
      isPaused = !isPaused;
      pauseButton.textContent = isPaused
        ? pauseButton.dataset.resumeLabel
        : pauseButton.dataset.pauseLabel;
      startCarousel();
    });

    carousel?.addEventListener("keydown", (event) => {
      if (event.key === "ArrowLeft") {
        event.preventDefault();
        showSlide(activeSlide - 1);
        startCarousel();
      }
      if (event.key === "ArrowRight") {
        event.preventDefault();
        showSlide(activeSlide + 1);
        startCarousel();
      }
    });

    showSlide(0);
    startCarousel();

    const creatorVideoTrack = root.querySelector("[data-creator-video-track]");
    const creatorVideoPrevious = root.querySelector("[data-creator-video-previous]");
    const creatorVideoNext = root.querySelector("[data-creator-video-next]");
    const creatorVideoCurrent = root.querySelector("[data-creator-video-current]");
    const creatorVideoProgress = root.querySelector("[data-creator-video-progress]");

    if (creatorVideoTrack) {
      const creatorVideoCards = [
        ...creatorVideoTrack.querySelectorAll(".creator-video-card"),
      ];

      const updateCreatorVideoControls = () => {
        const maxScroll = Math.max(
          0,
          creatorVideoTrack.scrollWidth - creatorVideoTrack.clientWidth,
        );
        if (creatorVideoPrevious) {
          creatorVideoPrevious.disabled = creatorVideoTrack.scrollLeft <= 2;
        }
        if (creatorVideoNext) {
          creatorVideoNext.disabled = creatorVideoTrack.scrollLeft >= maxScroll - 2;
        }

        const firstCard = creatorVideoCards[0];
        const gap = Number.parseFloat(
          window.getComputedStyle(creatorVideoTrack).columnGap || "0",
        );
        const distance = firstCard
          ? firstCard.getBoundingClientRect().width + gap
          : 1;
        const activeIndex = Math.min(
          Math.max(creatorVideoCards.length - 1, 0),
          Math.max(0, Math.round(creatorVideoTrack.scrollLeft / distance)),
        );

        if (creatorVideoCurrent) {
          creatorVideoCurrent.textContent = String(activeIndex + 1).padStart(2, "0");
        }
        if (creatorVideoProgress) {
          const progress = creatorVideoCards.length
            ? ((activeIndex + 1) / creatorVideoCards.length) * 100
            : 0;
          creatorVideoProgress.style.setProperty(
            "--creator-video-progress",
            `${progress}%`,
          );
        }
      };

      const scrollCreatorVideos = (direction) => {
        const firstCard = creatorVideoTrack.querySelector(".creator-video-card");
        if (!firstCard) return;
        const gap = Number.parseFloat(
          window.getComputedStyle(creatorVideoTrack).columnGap || "0",
        );
        const distance = firstCard.getBoundingClientRect().width + gap;
        creatorVideoTrack.scrollBy({
          left: direction * distance,
          behavior: prefersReducedMotion ? "auto" : "smooth",
        });
      };

      creatorVideoPrevious?.addEventListener("click", () => scrollCreatorVideos(-1));
      creatorVideoNext?.addEventListener("click", () => scrollCreatorVideos(1));
      creatorVideoTrack.addEventListener("scroll", updateCreatorVideoControls, {
        passive: true,
      });
      creatorVideoTrack.addEventListener("keydown", (event) => {
        if (event.key === "ArrowLeft") {
          event.preventDefault();
          scrollCreatorVideos(-1);
        }
        if (event.key === "ArrowRight") {
          event.preventDefault();
          scrollCreatorVideos(1);
        }
      });
      window.addEventListener("resize", updateCreatorVideoControls);
      updateCreatorVideoControls();
    }

    const reviewCards = [...root.querySelectorAll("[data-review-card]")];
    const reviewPages = [...root.querySelectorAll("[data-review-page]")];

    reviewPages.forEach((button, index) => {
      button.addEventListener("click", () => {
        reviewCards.forEach((card, cardIndex) => {
          card.classList.toggle("is-active", cardIndex === index);
        });
        reviewPages.forEach((page, pageIndex) => {
          page.classList.toggle("is-active", pageIndex === index);
        });
        reviewCards[index]?.scrollIntoView({
          behavior: prefersReducedMotion ? "auto" : "smooth",
          block: "nearest",
          inline: "center",
        });
      });
    });
  };

  const initializeAll = (scope = document) => {
    scope
      .querySelectorAll("[data-marsantsx-home]")
      .forEach((root) => initializeHome(root));
  };

  initializeAll();
  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", () => initializeAll());
  }

  document.addEventListener("shopify:section:load", (event) => {
    initializeAll(event.target);
  });
})();
